import React, { createContext, useContext, useReducer, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

const ACTIONS = {
  SET_ITEMS: 'SET_ITEMS',
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  UPDATE_COOKING_REQUEST: 'UPDATE_COOKING_REQUEST',
  CLEAR_CART: 'CLEAR_CART',
  SET_RESTAURANT: 'SET_RESTAURANT',
};

const initialState = {
  items: [],
  restaurant: null, // { id, name }
};

function cartReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_ITEMS:
      return { ...state, items: action.payload || [] };

    case ACTIONS.SET_RESTAURANT:
      return { ...state, restaurant: action.payload };

    case ACTIONS.ADD_ITEM: {
      const idx = state.items.findIndex(i => i.id === action.payload.id);
      if (idx >= 0) {
        const updated = [...state.items];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + action.payload.quantity };
        return { ...state, items: updated };
      }
      return { ...state, items: [...state.items, action.payload] };
    }

    case ACTIONS.REMOVE_ITEM:
      return { ...state, items: state.items.filter(i => i.id !== action.payload) };

    case ACTIONS.UPDATE_QUANTITY: {
      if (action.payload.quantity <= 0) {
        return { ...state, items: state.items.filter(i => i.id !== action.payload.id) };
      }
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.payload.id ? { ...i, quantity: action.payload.quantity } : i
        ),
      };
    }

    case ACTIONS.UPDATE_COOKING_REQUEST:
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.payload.id ? { ...i, cookingRequest: action.payload.cookingRequest } : i
        ),
      };

    case ACTIONS.CLEAR_CART:
      return { ...state, items: [], restaurant: null };

    default:
      return state;
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const addToCartWithRestaurant = useCallback((item, restaurant) => {
    if (state.restaurant && state.restaurant.id !== restaurant.id && state.items.length > 0) {
      // Return conflict so caller can show modal
      return { conflict: true, restaurant };
    }
    if (!state.restaurant || state.items.length === 0) {
      dispatch({ type: ACTIONS.SET_RESTAURANT, payload: restaurant });
    }
    dispatch({ type: ACTIONS.ADD_ITEM, payload: { ...item, quantity: item.quantity || 1 } });
    return { conflict: false };
  }, [state.restaurant, state.items.length]);

  const replaceCartAndAdd = useCallback((item, restaurant) => {
    dispatch({ type: ACTIONS.CLEAR_CART });
    dispatch({ type: ACTIONS.SET_RESTAURANT, payload: restaurant });
    dispatch({ type: ACTIONS.ADD_ITEM, payload: { ...item, quantity: item.quantity || 1 } });
  }, []);

  const removeFromCart = useCallback((itemId) => {
    dispatch({ type: ACTIONS.REMOVE_ITEM, payload: itemId });
  }, []);

  const updateQuantity = useCallback((itemId, quantity) => {
    dispatch({ type: ACTIONS.UPDATE_QUANTITY, payload: { id: itemId, quantity } });
  }, []);

  const updateCookingRequest = useCallback((itemId, cookingRequest) => {
    dispatch({ type: ACTIONS.UPDATE_COOKING_REQUEST, payload: { id: itemId, cookingRequest } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_CART });
  }, []);

  const reorderItems = useCallback((items, restaurant) => {
    dispatch({ type: ACTIONS.CLEAR_CART });
    dispatch({ type: ACTIONS.SET_RESTAURANT, payload: restaurant });
    items.forEach(item => {
      dispatch({ type: ACTIONS.ADD_ITEM, payload: { ...item, quantity: item.quantity || 1 } });
    });
  }, []);

  const getCartTotal = useCallback(() => {
    return state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [state.items]);

  const cartItemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems: state.items,
        cartRestaurant: state.restaurant,
        cartItemCount,
        addToCartWithRestaurant,
        replaceCartAndAdd,
        removeFromCart,
        updateQuantity,
        updateCookingRequest,
        clearCart,
        reorderItems,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
